import { Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowUpRight } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';
import { Eyebrow, Squiggle } from '../primitives';

type Solution = {
	badge: string;
	chip: string;
	title: string;
	description: string;
	photo: string;
	src: string;
	/**
	 * Draw `src` as the hand-off's inset PANEL rather than as a photo filling the card. The Solidarity
	 * Portal is the one card whose artwork is a screenshot of the product, and the hand-off frames it as
	 * a browser panel anchored to the card's right and bleeding off its bottom edge — full-bleed it read
	 * at nearly twice the size the design draws it at, and its own page header collided with the tab.
	 */
	panel?: boolean;
};

// Every LENGTH in the deck — sticky offset, tab height, card height, gap — lives in
// `.new-stack` in ../../-styles/theme.css, because the scroll-driven depth only lines up if all of
// them agree, and they are responsive besides. This file supplies each card's index and the depth
// it ends up at; the stylesheet turns that into a scroll range.
const CARD_HEIGHT = 'h-[var(--stack-card-h)]';

/**
 * The card's box in CSS pixels at `lg`, and the box the photo is REQUESTED at.
 *
 * It has to be the card's own proportion rather than a convenient 16:9, because `Image` sends BOTH
 * dimensions to imgproxy with `rt:fill` — so a requested aspect that differs from the card's is a
 * first crop, which `object-cover` then crops a second time. The two compound: at 16:9 the CDN cut
 * a third of the frame away before the browser cut a third of what was left, which is why the
 * photos read far tighter than the hand-off's. Matching the card makes both crops the same crop.
 *
 * 480 is `--stack-card-h` at `lg` and 1360 the section's widest content box (the 1440 max width
 * less its two 40px gutters); they live in ../../-styles/theme.css and here, which is the price of
 * the request needing numbers a stylesheet variable cannot hand it.
 */
const CARD_W = 1360;
const CARD_H = 480;

/**
 * Where the Solidarity Portal's panel sits, as fractions of the card. The hand-off gives it as
 * `right: 56.64px; top: 56.75px; width: 738.731px` in a 1352x479 card, plus an 8.987px white border
 * that Figma strokes OUTSIDE that box — so what the export contains, and what these numbers place, is
 * the 756.7px-wide panel including its frame: right 3.52%, top 9.97%, width 55.97%.
 *
 * Its box is 654.25 tall, far more than the room below it; Figma clips the export at the card's edge,
 * so the asset already ends where the card does and `overflow-hidden` only has to trim the last pixel.
 */
const PANEL = { left: '40.5%', top: '9.94%', width: '55.99%' };
/** The exported panel's own proportion — asked for exactly, so the CDN never crops it. */
const PANEL_W = 1514;
const PANEL_H = 863;

// Depth feel — as cards pile on top of a buried one it recedes: a touch smaller and darker per
// layer. These four numbers only fix where a card ENDS UP once fully buried; getting it there is
// the scroll timeline's job, which is why there is no scroll listener in this file. That also rules
// out spring smoothing by construction, which is the right outcome: a spring integrates towards its
// target, so it reaches the depth the scroll asked for only *after* the scroll asked for it, and the
// faster you scroll the further behind it falls. Tied to the scroll position instead, the pile is
// exactly as attached to the wheel as the sticky offsets are.
const SHRINK_PER_LAYER = 0.04; // scale removed per card stacked on top
const MIN_SCALE = 0.88;
const DIM_PER_LAYER = 0.085; // ink veil opacity added per layer
const MAX_DIM = 0.26;

/**
 * The photos are the hand-off's own, cut to the exact window its image fill shows.
 *
 * Cards 1-3 ARE full-bleed in the design — each one's `<img>` sits in a wrapper covering the whole card
 * — but the fill carries a crop transform, and that is the part `object-cover` cannot guess: it centres,
 * and the designer did not. Read off the fill boxes (as a % of each card, from the hand-off's own frames
 * 559:6976 / 6993 / 7003), the windows are
 *
 *   1 Tiendas físicas        left -2.72%  top -83.24%   w 105.47%  h 198.49%  → x 2.58%  y 41.94%  w 94.81%
 *   2 eCommerce              left  0%     top -178.25%  w 100%     h 376.34%  → x 0%     y 47.36%  w 100%
 *   3 Nómina y facturas      left -1.17%  top -263.91%  w 103.73%  h 439.14%  → x 1.13%  y 60.10%  w 96.40%
 *
 * where the right-hand triple is the same window as a fraction of the PHOTO. All three come out at the
 * card's own 2.82:1, which is what makes baking them into the file the faithful move: the asset then IS
 * the design's frame, `object-cover` has nothing left to choose, and the crop survives every breakpoint.
 * Cards 1 and 3 are also cropped ~5% and ~3.6% horizontally, which a cover never does.
 *
 * They are NEW filenames rather than replacements: the CDN derivative URL is keyed on the source path,
 * not on its contents, so overwriting `solution-pos.jpg` would keep serving the old crop until someone
 * purged it. The `-v2` pair exists for that same reason — see below.
 *
 * THE HAND-OFF SWAPS THE MIDDLE TWO PHOTOS, and we deliberately do not follow it. Frame 559:6993 is
 * chipped "2 eCommerce" but shows the till receipt, and 559:7003 is chipped "3 Redondeo en nómina y
 * facturas" but shows the laptop. That region of the file is demonstrably unfinished: 559:7003 still
 * carries card 2's title ("Euro Solidario") and card 2's description, so the frame was duplicated and
 * only its chip updated. Three things say the photos were left behind in that duplication — a till
 * receipt IS a factura, the earlier hand-off shipped `solution-ecommerce.jpg` as a laptop checkout and
 * `solution-payroll.jpg` as receipts, and `solutions-data.ts` still labels that payroll asset "Someone
 * going through their receipts". Tania reported the swap on TEC-3830 against the live page.
 *
 * So the first cut of these assets was faithful to the design and wrong on the page. The fix is the
 * ASSIGNMENT, not the crops: each photo keeps the window derived for it above, which is what the same
 * review praised ("queda precioso de zoom"). Anyone re-deriving these from Figma will reintroduce the
 * swap unless the design is corrected upstream first.
 */

/** "Cómo generamos impacto en cada compra" + the stackable (sticky) solution cards. */
export function Solutions() {
	const { t } = useLingui();
	const solutions: Solution[] = [
		{
			badge: '1',
			chip: t`Physical stores`,
			title: t`Charity Rounding Up`,
			description: t`The customer adds a few cents to their card purchase and donates them to a social cause. Those cents add up and go to the solidarity project your company chooses.`,
			photo: t`A card terminal asking the customer to round up their purchase`,
			src: '/redesign/solution-pos-wide.jpg',
		},
		{
			badge: '2',
			chip: t`eCommerce`,
			title: t`Solidarity Euro`,
			description: t`A fixed microdonation or round-up one click before checkout. Without interrupting the flow, frictionless.`,
			photo: t`An online checkout offering the Solidarity Euro before payment`,
			src: '/redesign/solution-ecommerce-wide-v2.jpg',
		},
		{
			badge: '3',
			chip: t`Payroll & invoices rounding`,
			title: t`Payroll & invoices`,
			description: t`Automated recurring donations through the cents of salaries or monthly subscriptions.`,
			photo: t`Hands with a receipt and calculator`,
			src: '/redesign/solution-payroll-wide-v2.jpg',
		},
		{
			badge: '4',
			chip: t`Solidarity Portal`,
			title: t`Solidarity Portal`,
			description: t`A tailor-made platform so your employees can join corporate or emergency campaigns.`,
			photo: t`A company's solidarity portal showing a live campaign`,
			src: '/redesign/solution-portal-panel.png',
			panel: true,
		},
	];

	return (
		<section className="mx-auto max-w-[1440px] px-6 pt-8 pb-16 md:pb-24 xl:px-10">
			{/* Section intro — scrolls away as usual. Keeping it out of the sticky context is what makes
			    the stack locale-proof: the cards no longer have to guess a header height that changes
			    when the title wraps to two lines in some languages. */}
			<div className="mx-auto mb-10 flex max-w-4xl flex-col items-center gap-4 text-center md:mb-14">
				<Eyebrow>{t`Our solutions`}</Eyebrow>
				<h2 className="text-3xl font-medium tracking-tight md:text-5xl">
					{t`How we generate`} <Squiggle>{t`impact`}</Squiggle> {t`on every purchase`}
				</h2>
				<p className="text-ink text-base md:text-lg">
					{t`We offer tailor-made tech solutions that integrate into your company's purchase flow, frictionless and fully transparent.`}
				</p>
			</div>

			{/* `new-stack` carries the deck's geometry AND names the view timeline every card animates
			    against — the deck is the subject, so one scroll drives the whole pile. */}
			<div className="new-stack flex flex-col gap-[var(--stack-gap)]">
				{solutions.map((s, i) => (
					<StackCard key={s.badge} solution={s} index={i} count={solutions.length} />
				))}
				{/* Settle zone — trailing scroll room so the last card locks onto the pile and the whole
				    assembled deck holds a beat, then releases and scrolls away together. Without it the
				    top card — being lowest in the flow — is the first one the container's rising bottom
				    edge drags off, so it appears to pass over the rest. */}
				<div aria-hidden className="h-[8rem] md:h-[18rem]" />
			</div>
		</section>
	);
}

function StackCard(props: { solution: Solution; index: number; count: number }) {
	const { index, count } = props;
	// How many cards end up stacked on top of this one once the deck is fully assembled. The front
	// card is buried by every card after it; the last one is never buried at all, so it never moves.
	const layers = count - 1 - index;

	const style = {
		// One tab-height below the card before it, so the tab (number + label) of everything already
		// parked stays readable as the deck piles up.
		'top': `calc(var(--stack-top) + ${index} * var(--stack-tab))`,
		// Scaling from the top edge is what makes a buried card read as *behind* the one covering it
		// rather than merely smaller: its top stays pinned under the tab above while its body pulls in.
		'transformOrigin': 'center top',
		// The stylesheet turns these two into the slice of the deck's scroll this card recedes over:
		// from the step on which this card gets buried to the step on which the deck stops growing.
		'--stack-i': index,
		'--stack-last': count - 1,
		'--stack-end-scale': Math.max(MIN_SCALE, 1 - layers * SHRINK_PER_LAYER),
		'--stack-end-veil': Math.min(MAX_DIM, layers * DIM_PER_LAYER),
	} as CSSProperties;

	return (
		<div className="new-stack-card sticky" style={style}>
			<SplitCard solution={props.solution}>
				{/* Ink veil — darkens the card as it is buried deeper, reinforcing the depth of the pile.
				    Always rendered, and transparent until the animation drives it: a browser without
				    scroll timelines, or a visitor who asked for reduced motion, simply never sees it. */}
				<div aria-hidden className="new-stack-veil bg-ink pointer-events-none absolute inset-0 opacity-0" />
			</SplitCard>
		</div>
	);
}

/**
 * The solution card, and now the only one: the photo fills it and an apricot wash sweeps diagonally out
 * of the bottom-left corner, so the copy sits on flat brand colour while the image still reads across
 * the right half. Tab pinned top-left, copy vertically centred under it.
 */
function SplitCard(props: { solution: Solution; children?: ReactNode }) {
	// `useLingui()` must be called here: the `t` macro only transforms `t`...`` when `t`
	// comes from the hook in this component (a passed-in `t` renders empty).
	const { t } = useLingui();
	const s = props.solution;
	return (
		<article
			className={cn(
				// No background HERE, deliberately: the ground is painted by the promoted layer below and
				// nowhere else. An `<article>` that paints its own too is a SECOND surface under the same
				// rounded clip, antialiased separately — and where the two differ, which is exactly what a
				// black ground under an apricot sweep is, the darker one leaks into the arc's partial-
				// coverage pixels. Measured on this construction: both painting = -114 at the arc, the
				// layer alone = 0.
				'text-ink relative overflow-hidden rounded-3xl shadow-[0_-14px_40px_-16px_rgba(37,48,44,0.22)]',
				CARD_HEIGHT,
			)}
		>
			{/* Everything the card is made of sits on ONE promoted layer, and `transform-gpu` is what
			    promotes it. Without it the rounded clip antialiases every layer SEPARATELY: at the arc the
			    apricot wash covers the boundary pixel only fractionally, so it cannot hide the dark photo
			    beneath it, and the photo lands in that pixel at a weight it never has anywhere inside the
			    card — one pixel of photo tracing the curve, DARKER than the card's own fill. That is the
			    dark hairline on the corners. Measured on card 1's corner it was rgb(200,185,170) against a
			    rgb(247,215,187) fill, and hiding the photo removed it; the straight edges never showed it
			    because they land on whole pixels, where coverage is 0 or 1.
			    Promotion rasterises the layers into one opaque texture first, so the clip has a single
			    finished colour to antialias. Nothing else tried in a probe of the same construction moved
			    the measurement: `clip-path: inset(0 round …)` instead of `overflow-hidden`, `isolation`,
			    hoisting the radius and the clip to the wrapper, and this very wrapper WITHOUT the
			    promotion all still measured the same undershoot. */}
			{/* The hand-off grounds the Solidarity Portal's card in BLACK and lays the same apricot sweep
			    over it, so the corner the sweep has cleared by — the one behind the panel — reads dark
			    instead of peach. Every other card is grounded in apricot, where the photo does that job. */}
			<div className={cn('absolute inset-0 transform-gpu', s.panel ? 'bg-black' : 'bg-apricot')}>
				{s.panel ? (
					// `[&>picture]:block` because `Image` wraps the img in a `<picture>`, which is inline: left
					// as such it adds a baseline gap under the panel and its shrink-to-fit width fights the
					// percentage below it.
					// `overflow-hidden` + the radius because the EXPORT HAS NO ALPHA: Figma flattened it onto
					// its own canvas, so the panel's rounded corners arrive as opaque #CCCCCC wedges — 43px
					// deep at the corner, gone by 50px, i.e. exactly the 25px radius the design gives it
					// (16.416 inner + the 8.987 white stroke Figma draws outside). Clipping to that radius
					// removes the grey and leaves the soft shadow the export also carries. Keying the grey
					// out of the file instead would only trade it for a fringe: the alpha it was composited
					// with is not recoverable.
					<div style={PANEL} className="absolute overflow-hidden rounded-[25px] [&>picture]:block">
						<Image
							src={s.src}
							alt={s.photo}
							height={PANEL_H / 2}
							aspectRatio={PANEL_W / PANEL_H}
							objectFit="contain"
							className="h-auto w-full"
						/>
					</div>
				) : (
					<Image
						src={s.src}
						alt={s.photo}
						height={CARD_H}
						aspectRatio={CARD_W / CARD_H}
						objectFit="cover"
						className="absolute inset-0 h-full w-full object-cover!"
					/>
				)}
				{/* The apricot wash runs in a different direction per breakpoint, and the two never overlap.
			    `apricot`, not `peach` — see the token note in theme.css.
			    Desktop sweeps diagonally out of the bottom-left corner — where the copy sits — and runs
			    the full width, clearing only at the opposite corner. That sweep cannot work on a phone —
			    the card is barely wider than the copy, so it would wash the photo out entirely — so
			    mobile fades from the top and bottom edges instead, leaving the middle of the photo
			    visible between the tab and the copy. */}
				<div aria-hidden className="from-apricot absolute inset-0 bg-gradient-to-b to-transparent to-30% md:hidden" />
				<div
					aria-hidden
					className="from-apricot via-apricot/85 absolute inset-0 bg-gradient-to-t from-50% via-70% to-transparent md:hidden"
				/>
				{/* 68deg with a 25% stop, fitted to the hand-off's export. A fixed angle, not `to-tr`: a corner
			    keyword follows the card's own diagonal and only matched at `xl`. */}
				<div
					aria-hidden
					className="from-apricot absolute inset-0 hidden bg-linear-68 from-25% to-transparent md:block"
				/>

				<div className="relative flex h-full flex-col">
					{/* Badge tab — its height IS --tab, so it lines up exactly with the peeking strip left
				    visible when the next card stacks on top. */}
					<div className="flex h-[var(--stack-tab)] shrink-0 items-center gap-2.5 px-6">
						<span className="bg-brand grid size-6 shrink-0 place-items-center rounded-md text-sm font-semibold text-black tabular-nums">
							{s.badge}
						</span>
						<span className="text-ink text-sm font-medium">{s.chip}</span>
					</div>

					{/* The padding is the floor for when a long locale leaves no free space for `my-auto`. */}
					{/* 24px in, at every width and on every card: the hand-off puts the tab, the title, the body
					    and the CTA all on the same 1.775% left edge (17.32px on its 976-wide card, 24px on the
					    1352-wide one), which is the `px-6` the tab row above already uses. `md:px-12` doubled
					    that from `md` up and pulled the copy off the line the tab sits on. */}
					<div className="my-auto max-w-xl px-6 py-8 md:py-12">
						<h3 className="text-3xl font-medium tracking-tight text-balance md:text-5xl">{s.title}</h3>
						<p className="text-ink mt-4 max-w-md md:text-lg">{s.description}</p>
					</div>

					{/* Its own row, so it holds the bottom edge and the copy centres between it and the tab. */}
					<div className="px-6 pb-8 md:pb-12">
						<Link
							to="/www/contact"
							className="hover:text-brand-strong flex w-fit items-center gap-1.5 text-sm font-medium transition-colors"
						>
							{t`Contact us`}
							<ArrowUpRight className="size-4" />
						</Link>
					</div>
				</div>

				{props.children}
			</div>
		</article>
	);
}
